
import React, { useState, useEffect } from 'react';
import { Mail, Rss, Key, ShieldCheck, Terminal, Send, Command, RefreshCw, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { sendContactMessage } from '../services/dataService';

// EMAIL ROUTING MAP (Used for visual terminal logs only now)
const EMAIL_ROUTING: Record<string, string> = {
    'Collaborazione': 'info@cyberumanita.com',
    'Segnalazione Errore': 'sysop@cyberumanita.com',
    'Proposta Contenuto': 'mind@cyberumanita.com',
    'Altro': 'info@cyberumanita.com'
};

const Contacts: React.FC = () => {
  const [formState, setFormState] = useState({ name: '', email: '', subject: 'Collaborazione', message: '' });
  const [status, setStatus] = useState<'IDLE' | 'ENCRYPTING' | 'TRANSMITTING' | 'SENT'>('IDLE');
  const [consoleLog, setConsoleLog] = useState<string[]>([]);
  
  // CAPTCHA STATE
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaStatus, setCaptchaStatus] = useState<'PENDING' | 'VALID' | 'INVALID'>('PENDING');

  // Generate Captcha on Mount
  useEffect(() => {
      generateCaptcha();
  }, []);

  const generateCaptcha = () => {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789#@&';
      let result = '';
      for (let i = 0; i < 6; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setCaptchaToken(result);
      setCaptchaInput('');
      setCaptchaStatus('PENDING');
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.toUpperCase();
      setCaptchaInput(val);
      if (val.length === captchaToken.length) {
          if (val === captchaToken) {
              setCaptchaStatus('VALID');
          } else {
              setCaptchaStatus('INVALID');
          }
      } else {
          setCaptchaStatus('PENDING');
      }
  };

  const addLog = (msg: string) => setConsoleLog(prev => [...prev, msg]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (captchaStatus !== 'VALID') {
        setCaptchaStatus('INVALID');
        return;
    }

    if(!formState.message || !formState.email) return;

    // VISUAL ROUTING EFFECT (Technical flavor)
    const visualRecipient = EMAIL_ROUTING[formState.subject] || 'info@cyberumanita.com';

    setStatus('ENCRYPTING');
    setConsoleLog([]);
    
    addLog(`> INITIATING HANDSHAKE WITH ${formState.email}...`);
    await new Promise(r => setTimeout(r, 800));
    addLog(`> GENERATING SESSION KEY... OK`);
    addLog(`> ROUTING PROTOCOL: ${formState.subject.toUpperCase()}`);
    addLog(`> TARGET NODE: ${visualRecipient}`);
    addLog(`> ENCRYPTING PAYLOAD (${formState.message.length} BYTES)...`);
    
    setStatus('TRANSMITTING');
    await new Promise(r => setTimeout(r, 1000));
    addLog(`> CONNECTING TO SERVER (WP-JSON)...`);
    
    // ATTEMPT REAL CONNECTION TO WORDPRESS
    // Note: We removed the recipient argument. WP CF7 will handle the "To" field statically.
    const success = await sendContactMessage(formState);
    
    if (success) {
        addLog(`> PAYLOAD DELIVERED TO NODE SYSOP... 100%`);
        setStatus('SENT');
        addLog(`> TRANSMISSION COMPLETE. SESSION CLOSED.`);
    } else {
        addLog(`> ERROR: SERVER CONNECTION REFUSED (CHECK FORM ID).`);
        addLog(`> FALLBACK: RETRYING LATER.`);
    }
    
    // Reset
    setTimeout(() => {
        setStatus('IDLE');
        setFormState({ name: '', email: '', subject: 'Collaborazione', message: '' });
        setConsoleLog([]);
        generateCaptcha(); // Regen captcha
    }, 4000);
  };

  return (
    <div className="min-h-screen bg-white pt-32 px-6 md:px-12 pb-20 font-sans text-black selection:bg-black selection:text-white">
      <div className="max-w-6xl mx-auto">
        
        <header className="mb-20 border-b-2 border-black pb-8">
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-gray-500 mb-4 font-sans">
                <Command size={14} />
                Nodo di Comunicazione
            </div>
            <h1 className="text-6xl md:text-8xl font-serif leading-none tracking-tighter">UPLINK</h1>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {/* COLONNA SINISTRA: HUMAN LAYER (Filosofia & PGP) */}
          <div className="space-y-12 animate-fade-in">
            <div>
                <h2 className="text-3xl font-serif italic mb-6 leading-tight">"Il segnale nel rumore."</h2>
                <p className="text-xl text-gray-600 font-light leading-relaxed mb-8 font-sans">
                In un’epoca che corre, noi rallentiamo per capire. Cyberumanità è un luogo dove la tecnologia torna ad avere senso: si discute, si verifica, si mette in prospettiva. Usa questo canale per proporre ricerche, segnalare criticità etiche, condividere casi e chiedere l’accesso come Contributor.
                </p>
                <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-black font-sans">
                    <div className="w-2 h-2 bg-black animate-pulse rounded-full"></div>
                    Tempo di risposta medio: 24h
                </div>
            </div>
            
            <div className="space-y-8 border-t border-gray-200 pt-8">
              <div className="flex items-start gap-4 group cursor-pointer">
                <Mail className="w-6 h-6 mt-1" />
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 font-sans">Protocollo Email</h3>
                  <a href="mailto:sysop@cyberumanita.com" className="text-2xl font-sans font-bold hover:underline decoration-2 underline-offset-4 block tracking-tight">sysop@cyberumanita.com</a>
                </div>
              </div>
              
               <div className="flex items-start gap-4 group cursor-pointer">
                <Rss className="w-6 h-6 mt-1" />
                <div>
                  <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1 font-sans">Network</h3>
                  <a href="https://substack.com/@cyberumanita" target="_blank" rel="noopener noreferrer" className="text-2xl font-sans font-bold hover:underline decoration-2 underline-offset-4 block tracking-tight">Cyberumanità Substack</a>
                </div>
              </div>
            </div>

            {/* PGP KEY BLOCK - STRICT B&W */}
            <div className="border-2 border-black p-6 relative bg-white hover:bg-gray-50 transition-colors">
                <div className="absolute -top-3 left-4 bg-white px-2 text-[10px] font-bold uppercase tracking-widest border border-black font-sans">
                    PGP Public Key
                </div>
                <pre className="font-mono text-[10px] leading-tight text-gray-600 break-all whitespace-pre-wrap">
{`-----BEGIN PGP PUBLIC KEY BLOCK-----
mQINBGI+... (CYBERUMANITÀ ROOT CA)
XO8s7a2b/9cKj1n0pqrstuvwxyZ
A3b4C5d6E7f8G9h0I1j2K3l4M5n6O7p
...END PGP PUBLIC KEY BLOCK-----`}
                </pre>
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-2 text-xs font-mono">
                    <ShieldCheck size={14} className="text-black"/> 
                    FINGERPRINT: <span className="font-bold">A1B2 C3D4 E5F6</span>
                </div>
            </div>
          </div>

          {/* COLONNA DESTRA: MACHINE LAYER (Paper Terminal Window) */}
          <div className="relative animate-slide-up delay-200">
            {/* WINDOW FRAME */}
            <div className="bg-white rounded-lg shadow-[8px_8px_0px_rgba(0,0,0,0.1)] overflow-hidden border-2 border-black">
                
                {/* WINDOW BAR (Grey Paper Style) - USES INTER (SANS) FOR UI ELEMENTS */}
                <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b-2 border-black">
                    <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full border border-black bg-white"></div>
                        <div className="w-3 h-3 rounded-full border border-black bg-white"></div>
                        <div className="w-3 h-3 rounded-full border border-black bg-white"></div>
                    </div>
                    <div className="text-[10px] font-sans text-black font-bold flex items-center gap-2 uppercase tracking-widest">
                        <Terminal size={12} />
                        SECURE_SHELL_V4
                    </div>
                    <div className="w-8"></div> {/* Spacer */}
                </div>

                {/* TERMINAL CONTENT - SWITCHED TO SANS (INTER) */}
                <div className="p-6 md:p-8 font-sans text-sm min-h-[500px] flex flex-col text-black bg-white relative">
                    
                    {/* Paper Texture Overlay for subtle effect */}
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-50 pointer-events-none"></div>

                    <div className="relative z-10 flex-grow flex flex-col">
                        {status === 'IDLE' ? (
                            <form className="space-y-6 flex-grow" onSubmit={handleSubmit}>
                                
                                <div className="mb-6 text-gray-500 text-xs border-b border-gray-200 pb-4 font-sans">
                                    Last login: {new Date().toLocaleString()} on ttys001<br/>
                                    Cyberumanità Secure Uplink initialized.<br/>
                                    Enter payload details below.<br/>
                                </div>

                                <div className="group">
                                    <label className="block text-[10px] font-bold text-black mb-1 font-sans uppercase tracking-wider">Identity ID (Name):</label>
                                    <div className="flex items-center border-b-2 border-gray-200 group-focus-within:border-black transition-colors">
                                        <span className="text-gray-300 mr-2 font-bold font-sans select-none">{'>'}</span>
                                        <input 
                                            type="text" 
                                            required
                                            value={formState.name}
                                            onChange={e => setFormState({...formState, name: e.target.value})}
                                            className="w-full bg-transparent border-none p-2 text-black font-bold focus:ring-0 placeholder-gray-300 outline-none font-sans text-lg"
                                            placeholder="Inserisci nome"
                                        />
                                    </div>
                                </div>

                                <div className="group">
                                    <label className="block text-[10px] font-bold text-black mb-1 font-sans uppercase tracking-wider">Target Vector (Email):</label>
                                    <div className="flex items-center border-b-2 border-gray-200 group-focus-within:border-black transition-colors">
                                        <span className="text-gray-300 mr-2 font-bold font-sans select-none">{'>'}</span>
                                        <input 
                                            type="email" 
                                            required
                                            value={formState.email}
                                            onChange={e => setFormState({...formState, email: e.target.value})}
                                            className="w-full bg-transparent border-none p-2 text-black font-bold focus:ring-0 placeholder-gray-300 outline-none font-sans text-lg"
                                            placeholder="user@domain.com"
                                        />
                                    </div>
                                </div>

                                <div className="group">
                                    <label className="block text-[10px] font-bold text-black mb-1 font-sans uppercase tracking-wider">Protocol (Subject):</label>
                                    <div className="flex items-center border-b-2 border-gray-200 group-focus-within:border-black transition-colors">
                                        <span className="text-gray-300 mr-2 font-bold font-sans select-none">{'>'}</span>
                                        <select 
                                            value={formState.subject}
                                            onChange={e => setFormState({...formState, subject: e.target.value})}
                                            className="w-full bg-transparent border-none p-2 text-black font-bold focus:ring-0 outline-none cursor-pointer appearance-none font-sans text-lg"
                                        >
                                            <option className="bg-white text-black">Collaborazione</option>
                                            <option className="bg-white text-black">Segnalazione Errore</option>
                                            <option className="bg-white text-black">Proposta Contenuto</option>
                                            <option className="bg-white text-black">Altro</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="group">
                                    <label className="block text-[10px] font-bold text-black mb-1 font-sans uppercase tracking-wider">Payload Data (Message):</label>
                                    <div className="border-2 border-gray-200 group-focus-within:border-black transition-colors p-2 bg-gray-50">
                                        <textarea 
                                            rows={5} 
                                            required
                                            value={formState.message}
                                            onChange={e => setFormState({...formState, message: e.target.value})}
                                            className="w-full bg-transparent border-none text-black font-medium focus:ring-0 outline-none resize-none placeholder-gray-300 font-sans text-base leading-relaxed"
                                            placeholder="Scrivi il tuo messaggio qui..."
                                        ></textarea>
                                    </div>
                                </div>

                                {/* --- CAPTCHA BLOCK --- */}
                                <div className="border border-gray-200 bg-gray-50 p-4 relative overflow-hidden">
                                     <div className="flex items-center justify-between mb-3">
                                         <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                                            <Lock size={12} /> Human Verification Protocol
                                         </div>
                                         <button type="button" onClick={generateCaptcha} className="text-black hover:rotate-180 transition-transform duration-500" title="Refresh Token">
                                             <RefreshCw size={14} />
                                         </button>
                                     </div>
                                     
                                     <div className="flex gap-4 items-center">
                                         {/* Token Display */}
                                         <div className="flex-1 bg-white border border-black p-3 text-center relative select-none overflow-hidden group cursor-crosshair">
                                             {/* Noise overlay */}
                                             <div className="absolute inset-0 opacity-20 pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMDAlJyBoZWlnaHQ9JzEwMCUnPjxmaWx0ZXIgaWQ9J24nPjxmZVR1cmJ1bGVuY2UgdHlwZT0nZnJhY3RhbE5vaXNlJyBiYXNlRnJlcXVlbmN5PScwLjUnIG51bU9jdGF2ZXM9JzEnIHN0aXRjaFRpbGVzPSJzdGl0Y2giLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0nMTAwJScgaGVpZ2h0PScxMDAlJyBmaWx0ZXI9InVybCgjbikiIG9wYWNpdHk9JzAuNScvPjwvc3ZnPg==')]"></div>
                                             
                                             <span className="font-mono text-2xl font-black tracking-[0.4em] text-black z-10 relative group-hover:blur-[1px] transition-all duration-200">
                                                 {captchaToken}
                                             </span>
                                         </div>
                                         
                                         {/* Input */}
                                         <div className="flex-1 relative">
                                             <input 
                                                type="text" 
                                                value={captchaInput}
                                                onChange={handleCaptchaChange}
                                                maxLength={6}
                                                className={`w-full bg-white border-2 p-3 font-mono text-center text-lg font-bold uppercase tracking-[0.2em] focus:outline-none transition-colors ${
                                                    captchaStatus === 'VALID' ? 'border-black text-black' : 
                                                    captchaStatus === 'INVALID' ? 'border-red-500 text-red-600' : 'border-gray-300'
                                                }`}
                                                placeholder="TYPE CODE"
                                             />
                                             <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                                 {captchaStatus === 'VALID' && <CheckCircle size={16} className="text-black" />}
                                                 {captchaStatus === 'INVALID' && <AlertCircle size={16} className="text-red-500" />}
                                             </div>
                                         </div>
                                     </div>
                                </div>

                                <button 
                                    type="submit"
                                    disabled={captchaStatus !== 'VALID'}
                                    className={`w-full font-bold py-4 uppercase tracking-widest transition-all flex items-center justify-center gap-2 mt-8 shadow-lg font-sans text-xs ${
                                        captchaStatus === 'VALID' 
                                        ? 'bg-black text-white hover:bg-gray-800 cursor-pointer' 
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    <Send size={14} /> {captchaStatus === 'VALID' ? 'Execute Transmission' : 'Awaiting Verification...'}
                                </button>

                            </form>
                        ) : (
                            // CONSOLE OUTPUT MODE (Light Mode)
                            <div className="flex flex-col h-full justify-end">
                                <div className="space-y-2 mb-4 text-xs md:text-sm font-sans">
                                    {consoleLog.map((log, i) => (
                                        <div key={i} className="animate-fade-in text-black font-medium border-l-2 border-black pl-2 font-sans">
                                            {log}
                                        </div>
                                    ))}
                                    {status === 'ENCRYPTING' && <div className="animate-pulse text-gray-500 font-bold font-sans">ENCRYPTING PACKETS...</div>}
                                    {status === 'TRANSMITTING' && <div className="animate-pulse text-gray-500 font-bold font-sans">UPLOADING TO SERVER...</div>}
                                    {status === 'SENT' && (
                                        <div className="mt-6 border-2 border-black p-6 text-center bg-gray-50">
                                            <div className="font-bold text-black text-lg mb-2 font-sans">TRANSMISSION SUCCESSFUL</div>
                                            <div className="text-[10px] text-gray-500 font-sans uppercase tracking-widest">LOG ID: {Math.random().toString(36).substring(7).toUpperCase()}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Terminal Cursor */}
                        {status === 'IDLE' && <div className="mt-auto pt-4 text-[10px] text-gray-400 flex justify-between border-t border-gray-100 font-sans uppercase tracking-widest">
                            <span>Ready to send.</span>
                            <span className="animate-pulse text-black font-bold">_</span>
                        </div>}
                    </div>
                </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contacts;
